/**
 * Admin "Issue Wash / Credit" form. Mirrors the owner-side form at
 * `app/owner/subscriptions/gifts/new.tsx` (free wash / % off / free minutes /
 * $ credit — absorbed the former promo-new and comp-credit twins 2026-07-22),
 * scoped to a specific owner via `?ownerId=` on every API call. Kept on the
 * simpler legacy single-tier scope; the owner form's checkbox scoping is a
 * superset the server also accepts.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useLocalSearchParams } from 'expo-router';
import Card from '../../../../../src/components/Card';
import Button from '../../../../../src/components/Button';
import Input from '../../../../../src/components/Input';
import FreeUpsellPicker from '../../../../../src/components/upsells/FreeUpsellPicker';
import type { UpsellCatalogItem } from '../../../../../src/components/upsells/UpsellPolicyPicker';
import Picker from '../../../../../src/components/Picker';
import { useToast } from '../../../../../src/components/Toast';
import ContentContainer from '../../../../../src/components/ContentContainer';
import { useTheme } from '../../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../../src/utils/theme';
import { spacing, fontSize, fontWeight } from '../../../../../src/utils/theme';
import { makeOwnerApi } from '../../../../../src/api/adminAsOwner';

interface Location { id: string; name: string }
interface WashTier { id: string; name: string; priceInCents: number; isActive: boolean }
interface RecipientMatch { userId: string; name: string | null; email: string; accountNumber: string; isMyCustomer: boolean }

export default function AdminIssueGiftScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { id: ownerId } = useLocalSearchParams<{ id: string }>();
  const ownerApi = useMemo(() => makeOwnerApi(ownerId ?? ''), [ownerId]);

  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);

  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientMatches, setRecipientMatches] = useState<RecipientMatch[]>([]);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recipientInputRef = useRef('');
  const [giftKind, setGiftKind] = useState<'free_wash' | 'percent' | 'free_minutes' | 'credit' | 'free_upsell'>('free_wash');
  const [tierId, setTierId] = useState('');
  const [percentOff, setPercentOff] = useState('25');
  // 1.4 R05 (admin twin of the owner gift form)
  const [discountScope, setDiscountScope] = useState<'wash' | 'wash_and_upsells'>('wash');
  const [freeUpsellIds, setFreeUpsellIds] = useState<string[]>([]);
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  const [creditDollars, setCreditDollars] = useState('10');
  const [freeMinutes, setFreeMinutes] = useState('5');
  const [locationId, setLocationId] = useState<string>('any');
  const [expirationOption, setExpirationOption] = useState('90');
  const [customDays, setCustomDays] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!ownerId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const locs = await ownerApi.get<Location[]>('/api/locations');
        if (ac.signal.aborted) return;
        setLocations(locs);
        const catalog = await ownerApi.get<WashTier[]>('/api/wash-tiers');
        if (ac.signal.aborted) return;
        const activeTiers = catalog.filter(t => t.isActive);
        setWashTiers(activeTiers);
        if (activeTiers.length > 0) setTierId(activeTiers[0].id);
        ownerApi.get<UpsellCatalogItem[]>('/api/upsells').then(list => { if (!ac.signal.aborted) setUpsellCatalog(list.filter(u => u.isActive !== false)); }).catch(() => {});
      } catch (err: any) {
        if (!ac.signal.aborted) toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
    return () => ac.abort();
  }, [ownerId]);

  /** Typeahead: fuzzy over the owner's customers + exact email/acct#/phone platform-wide. */
  const onRecipientInput = (text: string) => {
    setRecipientEmail(text);
    recipientInputRef.current = text;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = text.trim();
    if (q.length < 2) { setRecipientMatches([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await ownerApi.get<{ rows: RecipientMatch[] }>(`/api/customers/recipient-search?q=${encodeURIComponent(q)}`);
        if (recipientInputRef.current === text) setRecipientMatches(res.rows);
      } catch { /* silent */ }
    }, 300);
  };

  const getExpirationDays = () => {
    switch (expirationOption) {
      case '30': return 30;
      case '60': return 60;
      case '90': return 90;
      case 'custom': return Math.max(1, parseInt(customDays) || 90);
      default: return 90;
    }
  };

  const handleSubmit = async () => {
    if (!recipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid email address' });
      return;
    }

    // Credit ($): owner comp via wash-credit, not a voucher.
    if (giftKind === 'credit') {
      const cents = Math.round((parseFloat(creditDollars) || 0) * 100);
      if (cents < 100 || cents > 50000) {
        toast.show({ type: 'error', title: 'Error', message: 'Amount must be $1 – $500' });
        return;
      }
      try {
        setSubmitting(true);
        await ownerApi.post('/api/owner/wash-credit/comp', {
          recipientEmail: recipientEmail.trim().toLowerCase(),
          amountCents: cents,
          message: message.trim() || undefined,
        });
        toast.show({ type: 'success', title: 'Credit issued', message: `$${(cents / 100).toFixed(2)} added to ${recipientEmail}` });
        router.back();
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (giftKind === 'free_wash' && !tierId) {
      toast.show({ type: 'error', title: 'Error', message: 'Pick a wash tier' });
      return;
    }
    if (giftKind === 'percent') {
      const pct = parseInt(percentOff);
      if (!pct || pct < 1 || pct > 99) {
        toast.show({ type: 'error', title: 'Error', message: 'Percent must be 1–99 (use Free wash for 100%)' });
        return;
      }
    }
    if (giftKind === 'free_minutes') {
      const m = parseInt(freeMinutes);
      if (!m || m <= 0 || m > 60) {
        toast.show({ type: 'error', title: 'Error', message: 'Free minutes must be 1–60' });
        return;
      }
    }
    if (expirationOption === 'custom' && (!customDays || parseInt(customDays) <= 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter valid expiration days' });
      return;
    }

    try {
      setSubmitting(true);
      const body: any = {
        kind: 'gift',
        locationId: locationId === 'any' ? null : locationId,
        recipientEmail: recipientEmail.trim().toLowerCase(),
        expiresInDays: getExpirationDays(),
        message: message.trim() || undefined,
      };
      if (giftKind === 'free_minutes') {
        body.discountKind = 'free_minutes';
        body.bayType = 'self-serve';
        body.freeMinutes = parseInt(freeMinutes);
      } else {
        body.discountKind = giftKind;
        body.bayType = 'automatic';
        if (giftKind === 'free_wash') body.tierId = tierId;
        if (giftKind === 'percent') {
          body.percentOff = parseInt(percentOff);
          body.discountScope = discountScope;
          if (tierId) body.tierId = tierId;   // '' = any tier
        }
        if (giftKind === 'free_upsell') {
          if (freeUpsellIds.length === 0) { toast.show({ type: 'error', title: 'Error', message: 'Pick at least one free add-on' }); setSubmitting(false); return; }
          if (tierId) body.tierId = tierId;
        }
        if (freeUpsellIds.length && giftKind !== 'percent') body.freeUpsellIds = freeUpsellIds;
      }
      await ownerApi.post('/api/vouchers', body);
      toast.show({ type: 'success', title: 'Gift sent', message: `${recipientEmail} will get an email shortly.` });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const tierOptions = washTiers.map(t => ({ label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})`, value: t.id }));
  const locationOptions = [
    { label: 'Any location', value: 'any' },
    ...locations.map(l => ({ label: l.name, value: l.id })),
  ];
  const expirationOptions = [
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days (default)', value: '90' },
    { label: 'Custom', value: 'custom' },
  ];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Text style={styles.adminBanner}>Admin acting on behalf of owner {ownerId}</Text>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Recipient</Text>
          <Input
            label="Email — or search name / phone / account #"
            value={recipientEmail}
            onChangeText={onRecipientInput}
            placeholder="friend@example.com or Jane or USR-1234"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
          {recipientMatches.map(m => (
            <TouchableOpacity
              key={m.userId}
              style={styles.matchRow}
              onPress={() => { setRecipientEmail(m.email); recipientInputRef.current = m.email; setRecipientMatches([]); }}
            >
              <Ionicons name="person-circle-outline" size={20} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.matchName} numberOfLines={1}>{m.name || m.email}</Text>
                <Text style={styles.matchSub} numberOfLines={1}>{m.email} · {m.accountNumber}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>What to send</Text>
          <Picker
            label="Type"
            options={[
              { label: 'Free wash', value: 'free_wash' },
              { label: 'Percent off', value: 'percent' },
              { label: 'Free add-on with a wash', value: 'free_upsell' },
              { label: 'Free minutes (self-serve)', value: 'free_minutes' },
              { label: 'Credit ($)', value: 'credit' },
            ]}
            value={giftKind}
            onValueChange={(v) => setGiftKind(v as typeof giftKind)}
          />
          {giftKind === 'credit' ? (
            <Input label="Dollars ($1–$500)" value={creditDollars} onChangeText={setCreditDollars} placeholder="10" keyboardType="decimal-pad" />
          ) : giftKind === 'free_minutes' ? (
            <Input label="Free minutes" value={freeMinutes} onChangeText={setFreeMinutes} placeholder="5" keyboardType="number-pad" />
          ) : (
            <>
              {giftKind === 'percent' && (
                <>
                  <Input label="Percent off (1–99)" value={percentOff} onChangeText={setPercentOff} placeholder="25" keyboardType="number-pad" />
                  <Picker label="Discount covers" options={[{ label: 'The wash only', value: 'wash' }, { label: 'The wash and upsells', value: 'wash_and_upsells' }]} value={discountScope} onValueChange={v => setDiscountScope(v as 'wash' | 'wash_and_upsells')} />
                </>
              )}
              {giftKind !== 'percent' && <FreeUpsellPicker catalog={upsellCatalog} selected={freeUpsellIds} onChange={setFreeUpsellIds} title={giftKind === 'free_upsell' ? 'Free upsells' : 'Also free with this wash (optional)'} />}
              {tierOptions.length > 0 ? (
                <Picker
                  label={giftKind === 'free_upsell' ? 'Works with this tier' : 'Tier'}
                  options={giftKind === 'percent' || giftKind === 'free_upsell' ? [{ label: 'Any tier', value: '' }, ...tierOptions] : tierOptions}
                  value={tierId}
                  onValueChange={setTierId}
                />
              ) : (
                <Text style={styles.hintText}>No wash tiers yet for this owner.</Text>
              )}
            </>
          )}
          {giftKind !== 'credit' && (
            <Picker label="Valid at" options={locationOptions} value={locationId} onValueChange={setLocationId} />
          )}
        </Card>

        {giftKind !== 'credit' && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Expiration</Text>
          <Picker label="Expires in" options={expirationOptions} value={expirationOption} onValueChange={setExpirationOption} />
          {expirationOption === 'custom' && (
            <Input label="Days" value={customDays} onChangeText={setCustomDays} placeholder="45" keyboardType="number-pad" />
          )}
        </Card>
        )}

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Personal Message (optional)</Text>
          <Input
            value={message}
            onChangeText={setMessage}
            placeholder='e.g. "On us — sorry for the trouble"'
            multiline
            maxLength={1000}
            numberOfLines={3}
          />
        </Card>

        <Button title={giftKind === 'credit' ? 'Issue Credit' : 'Send Gift'} onPress={handleSubmit} loading={submitting} size="lg" style={{ marginTop: spacing.md }} />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', marginTop: spacing.xs },
  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },
  matchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  matchName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  matchSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
